import { useEffect, useRef, useState } from 'react';
import { findDrawerByRoute, DrawerType } from '../MenuConfig';
import { SIDEBAR_WIDTH } from '../styles';
import { ROUTES_PATH } from 'app/constants/Routes';

const MENU_DRAWER_EXTENDED_WIDTH = 270;
const MENU_DRAWER_COLLAPSED_WIDTH = 64;

const COLLAPSE_DRAWER_ON_ENTER_ROUTES: string[] = [
  ROUTES_PATH.CONTABILIDADE.LANDING_PAGE,
  ROUTES_PATH.JUSZAP.LANDING_PAGE,
  '/gpt/business',
  ROUTES_PATH.CLIENTES.CRIADOR_CONTEUDO,
];

const shouldCollapseDrawerOnEnter = (pathname: string): boolean =>
  COLLAPSE_DRAWER_ON_ENTER_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(route + '/'),
  );

type DrawerContext = DrawerType | null;

export function useSidebarDrawerState(
  pathname: string,
  onWidthChange: (width: number) => void,
  baseWidth: number = SIDEBAR_WIDTH,
  /**
   * Com `false`, nenhuma rota abre gaveta. É o caso do plano academy: o aluno
   * tem seis itens N1 e nenhum N2, mas /cursos também é item da gaveta
   * Clientes do advogado — sem isto, entrar em Cursos abria o N2 dela.
   */
  drawersEnabled: boolean = true,
) {
  const [activeDrawer, setActiveDrawer] = useState<DrawerContext>(null);
  const [isMenuDrawerCollapsed, setIsMenuDrawerCollapsed] = useState(false);
  const [isDrawerPinned, setIsDrawerPinned] = useState(false);
  const previousWidthRef = useRef<number | null>(null);

  useEffect(() => {
    const drawer = drawersEnabled ? findDrawerByRoute(pathname) : null;
    setActiveDrawer(drawer);
    setIsDrawerPinned(drawer !== null);
    if (drawer !== null && shouldCollapseDrawerOnEnter(pathname)) {
      setIsMenuDrawerCollapsed(true);
    }
  }, [pathname, drawersEnabled]);

  useEffect(() => {
    let width = baseWidth;
    if (activeDrawer && isDrawerPinned) {
      width += isMenuDrawerCollapsed
        ? MENU_DRAWER_COLLAPSED_WIDTH
        : MENU_DRAWER_EXTENDED_WIDTH;
    }
    if (previousWidthRef.current !== width) {
      onWidthChange(width);
      previousWidthRef.current = width;
    }
  }, [
    activeDrawer,
    isMenuDrawerCollapsed,
    isDrawerPinned,
    onWidthChange,
    baseWidth,
  ]);

  const toggleMenuCollapsed = () =>
    setIsMenuDrawerCollapsed((prev) => !prev);

  return {
    activeDrawer,
    setActiveDrawer,
    isMenuDrawerCollapsed,
    setIsMenuDrawerCollapsed,
    isDrawerPinned,
    setIsDrawerPinned,
    toggleMenuCollapsed,
    menuDrawerExtendedWidth: MENU_DRAWER_EXTENDED_WIDTH,
    menuDrawerCollapsedWidth: MENU_DRAWER_COLLAPSED_WIDTH,
  };
}
